import { useMemo } from 'react'
import { useTheme, Typography, Stack, Divider } from '@mui/material'
import { i18WithParams as t } from '@shared/locales'
import { Types } from 'service-api'
import { formatNumberAsPrice, isEmpty } from '@shared/common'
import {
  getPayFacTypeLabel,
  getPaymentPayFacTypesApplied,
  getInvoiceTxns,
  getTxnPayFacTypesApplied,
} from '@shared/payment'

export const Footer = ({
  receipt,
  dueToday,
  order,
}: {
  receipt: Types.ReceiptDto
  dueToday?: number | null
  order: Types.OrderClientDto | Types.OrderAdminDto
}) => {
  const { palette } = useTheme()
  const dueTodayPercentage = useMemo(() => {
    if (!receipt.total || !dueToday) return ''
    const totalVal = parseFloat(receipt.total.substring(1))
    const percent = totalVal ? Math.round((dueToday / totalVal) * 100) : 0
    return `${percent}%`
  }, [dueToday, receipt.total])
  const isEstimatedTotal = receipt.invoices?.some((inv) => inv.items?.some((item) => item.priceVaries))

  const deposit = order?.payments?.[0]
  const depositPayFacTypes = deposit ? getPaymentPayFacTypesApplied([deposit]) : []
  const invoicesWithRefund = order?.invoices?.filter((inv) => inv.refunded)
  const invoiceTxns = useMemo(() => getInvoiceTxns(order?.payments || []), [order?.payments])
  const orderCancelled = order?.orderStatus === 'CANCELED' || order?.orderStatus === 'PARTIALLY_CANCELED'

  const sxOrderCancelled = {
    textDecoration: 'line-through',
    color: 'text.secondary',
  }

  return (
    <>
      <Stack direction="row" justifyContent="space-between" mt={3}>
        <Typography variant="h5" sx={orderCancelled ? sxOrderCancelled : {}}>
          {t('global.label.total')}
        </Typography>
        <Stack direction={'row'} spacing={1} alignItems={'center'}>
          {isEstimatedTotal && (
            <Typography variant="h6" sx={{ color: palette.action.active }}>
              {t('global.label.estimated')}
            </Typography>
          )}
          <Typography variant="h5" sx={orderCancelled ? sxOrderCancelled : {}}>
            {receipt.total}
          </Typography>
        </Stack>
      </Stack>
      {!isEmpty(dueToday) && (
        <Stack direction="row" justifyContent="space-between" mt={3}>
          <Typography variant="h5">{t('global.label.dueToday', { percentage: dueTodayPercentage })}</Typography>
          <Typography variant="h5">{formatNumberAsPrice(dueToday!)}</Typography>
        </Stack>
      )}
      {orderCancelled && <Divider sx={{ mt: 2, borderStyle: 'dashed' }} />}
      <Stack mt={2} spacing={0.5}>
        <Typography variant="body2bold" sx={{ color: 'text.secondary' }}>
          {t('global.label.payments')}
        </Typography>
        {deposit?.step === 'CONFIRMED' && (
          <Stack spacing={0.5} direction="row" justifyContent="space-between">
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Typography variant="body1" component="span">
                {t('global.label.deposit')}
              </Typography>
              <Typography component="span" variant="body1bold" color="text.secondary">
                •
              </Typography>
              <Typography variant="body1" component="span" sx={{ color: 'text.secondary' }}>
                {depositPayFacTypes.map((type) => getPayFacTypeLabel(type)).join(', ')}
              </Typography>
            </Stack>
            <Typography variant="body1" component="span">
              {formatNumberAsPrice(deposit?.depositDue || 0)}
            </Typography>
          </Stack>
        )}
        {invoicesWithRefund?.map((invoice) => (
          <Stack key={`${invoice.displayName}-${invoice.id}`} direction="row" justifyContent="space-between">
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Typography variant="body1bold" color="error.main">
                {orderCancelled ? t('receipt.label.cancellationRefund') : t('global.label.refund')}
              </Typography>
              <Typography component="span" variant="body1bold" color="text.secondary">
                •
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {getTxnPayFacTypesApplied(invoiceTxns.get(invoice.id!) || [])
                  .map((type) => getPayFacTypeLabel(type))
                  .join(', ')}
              </Typography>
            </Stack>
            <Typography variant="body1bold" component="span" color="error.main">
              ({formatNumberAsPrice(invoice.refunded || 0)})
            </Typography>
          </Stack>
        ))}
      </Stack>
    </>
  )
}
